import { Table, Tag, Tooltip } from "antd";
import { PhoneOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AppointmentItem } from "../../../api/Appointment";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ScheduleDateCell from "../../ScheduleDateCell";
import useTableColumnSettings from "../../../hooks/useTableColumnSettings";
import dayjs from "dayjs";

// Cột checkbox chọn dòng: cố định độ rộng để tính đúng tổng chiều rộng bảng khi người dùng kéo cột
const SELECTION_COL_WIDTH = 48;

const EmptyCell = () => <span className="text-gray-400">—</span>;

/** Chữ một dòng, dài quá thì cắt kèm tooltip */
const TextCell = ({ value, maxWidth = 180 }: { value?: string | null; maxWidth?: number }) =>
    value ? (
        <span className="truncate inline-block align-middle" style={{ maxWidth }} title={value}>{value}</span>
    ) : <EmptyCell />;

const staffName = (s?: { name?: string; fullname?: string } | null) => s?.name || s?.fullname || "";

const formatDateTime = (value?: string | null) =>
    value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY HH:mm") : null;

interface Props {
    data: AppointmentItem[];
    loading?: boolean;
    pagination?: { current: number; pageSize: number; total: number };
    onPageChange?: (page: number) => void;
    /** Kết quả useLeadTable — trang cha giữ để đặt nút cài đặt cột lên toolbar */
    table: ReturnType<typeof useLeadTable>;
}

const statusTagMap: Record<number, JSX.Element> = {
    1: <Tag color="blue">Đặt lịch mới</Tag>,
    2: <Tag color="cyan">Đã xác nhận</Tag>,
    3: <Tag color="orange">Chờ thực hiện</Tag>,
    4: <Tag color="green">Đã thực hiện</Tag>,
    0: <Tag color="red">Hủy lịch</Tag>,
};

const InitialAvatar = ({ name }: { name: string }) => {
    const initials = name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <div className="w-6 h-6 rounded-full bg-[#3e91ab] text-xs flex items-center justify-center text-white shrink-0">
            {initials}
        </div>
    );
};

const NameAvatar = ({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) => {
    const [avatarError, setAvatarError] = useState(false);

    if (avatarUrl && !avatarError) {
        return (
            <img
                src={avatarUrl}
                alt={name}
                className="w-6 h-6 rounded-full object-cover shrink-0"
                onError={() => setAvatarError(true)}
            />
        );
    }

    return <InitialAvatar name={name} />;
};

/**
 * Cột + cài đặt cột (ẩn/hiện, thứ tự, cố định, độ rộng) của LeadTable.
 * Gọi ở trang cha để đặt <ColumnSettingButton {...settingProps} /> cạnh công tắc Bảng/Tiến trình.
 *
 * @param settingKey     key lưu trong extra_data — mỗi trang dùng bảng này một key riêng
 * @param detailBasePath đường dẫn trang chi tiết khi bấm tên khách hàng
 */
export const useLeadTable = (settingKey: string, detailBasePath = '/consultation/detail') => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const columns = [
        {
            // Bỏ cột mã lịch hẹn -> tên khách hàng là chỗ mở chi tiết
            key: "name",
            title: t("lead.col_name") || "Tên khách hàng",
            render: (_: any, record: AppointmentItem) => {
                const name = record.customer_name || record.channel_customer?.display_name || "—";
                const avatarUrl = record.channel_customer?.avatar || record.channel_customer?.avatar_url;
                return (
                    <div
                        className="flex items-center gap-2 cursor-pointer group"
                        onClick={() => navigate(`${detailBasePath}/${record.id}`)}
                    >
                        <NameAvatar name={name} avatarUrl={avatarUrl} />
                        <span className="font-medium text-[#3e91ab] group-hover:underline truncate" title={name}>
                            {name}
                        </span>
                    </div>
                );
            },
        },
        /* ----- Các cột theo "Frontend Column Mapping" của API /appointments -----
           Đều ẩn sẵn (defaultHidden) để giữ giao diện hiện tại; người dùng tự bật trong cài đặt cột. */
        {
            key: "code",
            title: t("order.col_order_code"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.code ? (
                <span
                    className="font-semibold text-[#3e91ab] cursor-pointer whitespace-nowrap"
                    onClick={() => navigate(`${detailBasePath}/${record.id}`)}
                >
                    {record.code}
                </span>
            ) : <EmptyCell />,
        },
        {
            key: "phone",
            title: t("lead.col_phone") || "Điện thoại",
            render: (_: any, record: AppointmentItem) => {
                const phone = record.customer_phone || record.channel_customer?.phone || "—";
                return phone !== "—" ? (
                    <div className="flex items-center gap-2 text-[#3e91ab]">
                        <PhoneOutlined />
                        <span>{phone}</span>
                    </div>
                ) : <span className="text-gray-400">—</span>;
            },
        },
        {
            key: "email",
            title: "Email",
            render: (_: any, record: AppointmentItem) => {
                const email = record.customer_email || record.channel_customer?.email || "";
                return email ? (
                    <Tooltip title={email}>
                        <span className="truncate max-w-[180px] inline-block text-gray-500">{email}</span>
                    </Tooltip>
                ) : <span className="text-gray-400">—</span>;
            },
        },
        {
            key: "owner",
            title: t("lead.col_owner") || "Nhân viên",
            render: (_: any, record: AppointmentItem) => {
                const name = record.branch_staff?.name || record.branch_staff?.fullname || record.staff?.name || record.staff?.fullname || "—";
                return name !== "—" ? (
                    <div className="flex items-center gap-2">
                        <InitialAvatar name={name} />
                        <span>{name}</span>
                    </div>
                ) : <span className="text-gray-400">—</span>;
            },
        },
        {
            key: "page_staff",
            title: t("customer.page_staff"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={staffName(record.staff)} />,
        },
        {
            key: "telesale",
            title: "Telesale",
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={staffName(record.telesale)} />,
        },
        {
            key: "branch_staff",
            title: t("order.col_branch_staff"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={staffName(record.branch_staff)} />,
        },
        {
            key: "action_staff",
            title: t("order.col_action_staff"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={staffName(record.action_staff)} />,
        },
        {
            key: "services",
            title: "Dịch vụ",
            render: (_: any, record: AppointmentItem) => {
                const services = record.services;
                if (!services || services.length === 0) return <span className="text-gray-400">—</span>;
                return (
                    <div className="max-h-[60px] overflow-y-auto space-y-0.5 custom-scrollbar max-w-[180px]">
                        {services.map((s) => (
                            <div key={s.id} className="text-xs text-gray-600 dark:text-gray-300 truncate" title={s.name}>
                                {s.name}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            key: "branch",
            title: t("order.col_branch"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={record.branch?.name} maxWidth={150} />,
        },
        {
            title: "Ngày hẹn",
            dataIndex: "scheduled_at",
            render: (val: string) => <ScheduleDateCell value={val} />,
        },
        {
            key: "status",
            title: t("lead.col_status") || "Trạng thái",
            render: (_: any, record: AppointmentItem) => {
                const statusNum = record.customer_tag_status ?? record.status;
                const statusText = record.customer_tag_status_text;
                if (statusText) return statusTagMap[statusNum] ? <Tag color={(statusTagMap[statusNum] as any).props.color}>{statusText}</Tag> : <Tag>{statusText}</Tag>;
                return statusTagMap[statusNum] || <Tag>{statusNum}</Tag>;
            },
        },
        {
            key: "customer_tag",
            title: t("order.col_customer_tag"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.customer_tag?.name ? (
                // align-middle: Tag có overflow hidden bị đẩy lệch lên so với các ô khác cùng hàng
                <Tag color={record.customer_tag.color || undefined} className="max-w-[180px] truncate align-middle" title={record.customer_tag.name}>
                    {record.customer_tag.name}
                </Tag>
            ) : <EmptyCell />,
        },
        {
            key: "care_status",
            title: t("order.col_care_status"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                if (record.is_cared === undefined && !record.care_status_text) return <EmptyCell />;
                const label = record.care_status_text || (record.is_cared ? "Đã chăm sóc" : "Chưa chăm sóc");
                return <Tag color={record.is_cared ? "green" : "default"}>{label}</Tag>;
            },
        },
        {
            key: "customer_gender",
            title: t("customer.col_gender"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const map: Record<number, string> = {
                    0: t("customer.gender_female"),
                    1: t("customer.gender_male"),
                    2: t("customer.gender_other"),
                };
                const g = record.customer_gender;
                return g !== null && g !== undefined && map[g] ? map[g] : <EmptyCell />;
            },
        },
        {
            key: "customer_dob",
            title: t("customer.col_birthday"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const d = record.customer_dob ? dayjs(record.customer_dob) : null;
                if (!d || !d.isValid()) return <EmptyCell />;
                return (
                    <span className="whitespace-nowrap">
                        {d.format("DD/MM/YYYY")}
                        <span className="text-gray-400 ml-1">({dayjs().diff(d, "year")} {t("customer.age_suffix")})</span>
                    </span>
                );
            },
        },
        {
            key: "is_foreign",
            title: t("customer.col_origin"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.is_foreign === 1
                ? <Tag color="purple">{t("customer.origin_foreign")}</Tag>
                : <span className="whitespace-nowrap">{t("customer.origin_domestic")}</span>,
        },
        {
            key: "address",
            title: t("customer.col_address"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={record.address} />,
        },
        {
            key: "ward",
            title: t("customer.col_ward"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={record.ward?.name || record.ward?.full_name} maxWidth={150} />,
        },
        {
            key: "province",
            title: t("customer.col_province"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={record.province?.name || record.province?.full_name} maxWidth={150} />,
        },
        {
            key: "note",
            title: t("order.col_note"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => record.note ? (
                <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{record.note}</span>}>
                    <div className="line-clamp-2 text-gray-500 dark:text-gray-400 max-w-[220px]">{record.note}</div>
                </Tooltip>
            ) : <EmptyCell />,
        },
        {
            key: "creator",
            title: t("order.col_created_by"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => <TextCell value={record.creator?.fullname} maxWidth={160} />,
        },
        {
            key: "created_at",
            title: t("customer.col_created_at"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const v = formatDateTime(record.created_at);
                return v ? <span className="whitespace-nowrap">{v}</span> : <EmptyCell />;
            },
        },
        {
            key: "updated_at",
            title: t("customer.col_updated_at"),
            defaultHidden: true,
            render: (_: any, record: AppointmentItem) => {
                const v = formatDateTime(record.updated_at);
                return v ? <span className="whitespace-nowrap">{v}</span> : <EmptyCell />;
            },
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    return useTableColumnSettings(settingKey, columns, { extraWidth: SELECTION_COL_WIDTH });
};

const LeadTable = ({ data, loading, pagination, onPageChange, table }: Props) => {
    return (
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl">
            <Table
                columns={table.columns}
                components={table.components}
                tableLayout={table.tableLayout}
                dataSource={data}
                rowKey="id"
                loading={loading}
                rowSelection={{ columnWidth: SELECTION_COL_WIDTH }}
                pagination={
                    pagination
                        ? {
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: onPageChange,
                            position: ["bottomRight"],
                            showSizeChanger: false,
                            showTotal: (total) => `Tổng ${total} lịch hẹn`,
                        }
                        : { pageSize: 10, position: ["bottomRight"], showSizeChanger: false }
                }
                size="middle"
                scroll={{ x: table.scrollX }}
                className="custom-ant-table"
            />
        </div>
    );
};

export default LeadTable;